// API Configuration Types
interface APIEndpoints {
  // Authentication
  LOGIN: string;
  REGISTER: string;
  LOGOUT: string;
  REFRESH: string;
  AUTH_ADMIN_REGISTRATION_STATS: string;

  // User Management
  USERS: string;
  USERS_ADMIN_ALL: string; // Admin endpoint for getting all users
  USERS_ADMIN_DETAIL: string; // Admin endpoint for getting user details
  USERS_ADMIN_RESET_PASSWORD: string; // Admin endpoint for resetting user passwords
  USERS_ADMIN_UPDATE_ROLE: string; // Admin endpoint for updating user roles
  USERS_ADMIN_ADD_DAYS: string; // Admin endpoint for adding days to user subscription
  USERS_ADMIN_BULK_ADD_DAYS: string; // Admin endpoint for bulk adding days to subscriptions
  USERS_ADMIN_IMPORT: string; // Admin endpoint for bulk user import (Excel)
  USERS_ADMIN_STUDENT_OPTIONS: string; // Admin endpoint for distinct courses and years for Assign Group
  USERS_ADMIN_FILTER_OPTIONS: string; // Admin endpoint for report filter options (provinces, cities, etc.)
  USERS_ADMIN_CONSULTANT_ASSIGNMENTS: string; // Base path for GET/PUT consultant-assignments/:consultantId
  PROFILES: string;
  MODULE_PROGRESS: string;

  // Assessments
  PHYSICAL_HEALTH: string;
  PHYSICAL_HEALTH_LATEST: string;
  MENTAL_HEALTH: string;
  EMOTIONAL_WELLBEING: string;
  EMOTIONAL_WELLBEING_LATEST: string;
  SPIRITUAL: string;
  NUTRITIONAL: string;
  EMOTIO_PSYCHO_RATIONAL: string;
  EMOTIO_PSYCHO_RATIONAL_LATEST: string;
  LEGENDARIAL: string;
  RELIGIO_TRADITIO_CULTURAL: string;
  SOCIO_RELATIONAL: string;
  SOCIO_RELATIONAL_LATEST: string;
  FINANCIAL: string;
  ENVIRONMENTAL: string;
  OCCUPATIONAL: string;
  STAR_OF_WELLBEING: string;
  STAR_OF_WELLBEING_LATEST: string;
  STAR_OF_WELLBEING_CONSULTANT: string;

  // Assessment Admin
  ASSESSMENTS_ADMIN_TOTAL_COUNT: string;
  ASSESSMENTS_ADMIN_OVERALL_SOWB_SCORE: string;
  ASSESSMENTS_ADMIN_WEIGHTED_WELLBEING_INDEX: string;
  ASSESSMENTS_ADMIN_SCORE_TRENDS: string;
  ASSESSMENTS_ADMIN_STAR_RADAR_AVERAGES: string;
  RESOURCES: string;

  // Chat
  CHAT: string;
  CHAT_HISTORY: string;
  WELLNESS_COMPANION: string;

  // Documents
  DOCUMENTS: string;

  // Consultations
  CONSULTATIONS: string;

  // Blood Tests
  BLOOD_TESTS: string;

  // Data Permissions
  DATA_PERMISSIONS: string;

  // Token Usage
  TOKEN_USAGE: string;
  COMPLETED_SESSIONS: string;

  // Billing & Subscriptions
  BILLING_PLANS: string;
  BILLING_PLANS_ADMIN: string; // Admin endpoint for managing plans
  BILLING_SUBSCRIPTION: string;
  BILLING_SUBSCRIPTION_PAUSE: string; // Admin endpoint for pausing subscriptions
  BILLING_SUBSCRIPTION_ADMIN_CREATE: string; // Admin endpoint for creating subscriptions
  BILLING_SUBSCRIPTION_ADMIN_DELETE: string; // Admin endpoint for deleting subscriptions
  BILLING_USAGE: string;
  BILLING_HISTORY: string;

  // Groups
  GROUPS: string;
  GROUP_FOLDERS: string;
  CONSULTANT_SELECT_OPTIONS: string;
  PROGRAMS: string;

  // Suggestions
  SUGGESTIONS: string;
  SUGGESTIONS_ADMIN: string;

  // Consultant Vulnerabilities
  CONSULTANT_VULNERABILITIES: string;

  // Admin Dashboard
  ADMIN_DASHBOARD_STATS: string;
  ADMIN_GROWTH_TREND: string;
  API_HEALTH: string;
}

interface APIConfig {
  BASE_URL: string;
  ENDPOINTS: APIEndpoints;
  ENVIRONMENT: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';

export const API_CONFIG: APIConfig = {
  BASE_URL: API_BASE_URL,
  ENDPOINTS: {
    // Authentication
    LOGIN: `${API_BASE_URL}/api/auth/login`,
    MODULE_PROGRESS: `${API_BASE_URL}/api/module-progress`,
    REGISTER: `${API_BASE_URL}/api/auth/register`,
    LOGOUT: `${API_BASE_URL}/api/auth/logout`,
    REFRESH: `${API_BASE_URL}/api/auth/refresh`,
    AUTH_ADMIN_REGISTRATION_STATS: `${API_BASE_URL}/api/auth/admin/registration-stats`,

    // User Management
    USERS: `${API_BASE_URL}/api/users`,
    USERS_ADMIN_ALL: `${API_BASE_URL}/api/users/admin/all`,
    USERS_ADMIN_DETAIL: `${API_BASE_URL}/api/users/admin`,
    USERS_ADMIN_RESET_PASSWORD: `${API_BASE_URL}/api/users/admin`,
    USERS_ADMIN_UPDATE_ROLE: `${API_BASE_URL}/api/users/admin`,
    USERS_ADMIN_ADD_DAYS: `${API_BASE_URL}/api/users/admin`,
    USERS_ADMIN_BULK_ADD_DAYS: `${API_BASE_URL}/api/users/admin/subscriptions`,
    USERS_ADMIN_IMPORT: `${API_BASE_URL}/api/users/admin/import`,
    USERS_ADMIN_STUDENT_OPTIONS: `${API_BASE_URL}/api/users/admin/student-options`,
    USERS_ADMIN_FILTER_OPTIONS: `${API_BASE_URL}/api/users/admin/filter-options`,
    USERS_ADMIN_CONSULTANT_ASSIGNMENTS: `${API_BASE_URL}/api/users/admin/consultant-assignments`,
    PROFILES: `${API_BASE_URL}/api/profiles`,

    // Assessments
    PHYSICAL_HEALTH: `${API_BASE_URL}/api/physical-health-assessments`,
    PHYSICAL_HEALTH_LATEST: `${API_BASE_URL}/api/physical-health-assessments/latest`,
    MENTAL_HEALTH: `${API_BASE_URL}/api/mental-health-assessments`,
    EMOTIONAL_WELLBEING: `${API_BASE_URL}/api/emotional-wellbeing-assessments`,
    EMOTIONAL_WELLBEING_LATEST: `${API_BASE_URL}/api/emotional-wellbeing-assessments/latest`,
    SPIRITUAL: `${API_BASE_URL}/api/spiritual-assessments`,
    NUTRITIONAL: `${API_BASE_URL}/api/nutritional-assessments`,
    EMOTIO_PSYCHO_RATIONAL: `${API_BASE_URL}/api/emotio-psycho-rational-assessments`,
    EMOTIO_PSYCHO_RATIONAL_LATEST: `${API_BASE_URL}/api/emotio-psycho-rational-assessments/latest`,
    LEGENDARIAL: `${API_BASE_URL}/api/legendarial-assessments`,
    RELIGIO_TRADITIO_CULTURAL: `${API_BASE_URL}/api/religio-traditio-cultural-assessments`,
    SOCIO_RELATIONAL: `${API_BASE_URL}/api/socio-relational-assessments`,
    SOCIO_RELATIONAL_LATEST: `${API_BASE_URL}/api/socio-relational-assessments/latest`,
    FINANCIAL: `${API_BASE_URL}/api/financial-assessments`,
    ENVIRONMENTAL: `${API_BASE_URL}/api/environmental-assessments`,
    OCCUPATIONAL: `${API_BASE_URL}/api/occupational-assessments`,
    STAR_OF_WELLBEING: `${API_BASE_URL}/api/dass/star-of-wellbeing-assessment`,
    STAR_OF_WELLBEING_LATEST: `${API_BASE_URL}/api/dass/star-of-wellbeing-assessment/latest`,
    STAR_OF_WELLBEING_CONSULTANT: `${API_BASE_URL}/api/dass/star-of-wellbeing-assessment/consultant`,

    // Assessment Admin
    ASSESSMENTS_ADMIN_TOTAL_COUNT: `${API_BASE_URL}/api/assessments/admin/total-count`,
    ASSESSMENTS_ADMIN_OVERALL_SOWB_SCORE: `${API_BASE_URL}/api/assessments/admin/overall-sowb-score`,
    ASSESSMENTS_ADMIN_WEIGHTED_WELLBEING_INDEX: `${API_BASE_URL}/api/assessments/admin/weighted-wellbeing-index`,
    ASSESSMENTS_ADMIN_SCORE_TRENDS: `${API_BASE_URL}/api/assessments/admin/score-trends`,
    ASSESSMENTS_ADMIN_STAR_RADAR_AVERAGES: `${API_BASE_URL}/api/assessments/admin/star-radar-averages`,
    RESOURCES: `${API_BASE_URL}/api/resources`,

    // Chat
    CHAT: `${API_BASE_URL}/api/chat`,
    CHAT_HISTORY: `${API_BASE_URL}/api/chat/history`,
    WELLNESS_COMPANION: `${API_BASE_URL}/api/chat/wellness`,

    // Documents
    DOCUMENTS: `${API_BASE_URL}/api/documents`,

    // Consultations
    CONSULTATIONS: `${API_BASE_URL}/api/consultations`,

    // Blood Tests
    BLOOD_TESTS: `${API_BASE_URL}/api/blood-tests`,

    // Data Permissions
    DATA_PERMISSIONS: `${API_BASE_URL}/api/data-permissions`,

    // Token Usage
    TOKEN_USAGE: `${API_BASE_URL}/api/token-usage`,
    COMPLETED_SESSIONS: `${API_BASE_URL}/api/completed-sessions`,

    // Billing & Subscriptions
    BILLING_PLANS: `${API_BASE_URL}/api/billing/plans`,
    BILLING_PLANS_ADMIN: `${API_BASE_URL}/api/billing/plans/admin`,
    BILLING_SUBSCRIPTION: `${API_BASE_URL}/api/billing/subscriptions`,
    BILLING_SUBSCRIPTION_PAUSE: `${API_BASE_URL}/api/billing/subscriptions/pause`,
    BILLING_SUBSCRIPTION_ADMIN_CREATE: `${API_BASE_URL}/api/billing/subscriptions/admin/create`,
    BILLING_SUBSCRIPTION_ADMIN_DELETE: `${API_BASE_URL}/api/billing/subscriptions/admin`,
    BILLING_USAGE: `${API_BASE_URL}/api/billing/usage`,
    BILLING_HISTORY: `${API_BASE_URL}/api/billing/usage/history`,

    // Groups
    GROUPS: `${API_BASE_URL}/api/groups`,
    GROUP_FOLDERS: `${API_BASE_URL}/api/group-folders`,
    CONSULTANT_SELECT_OPTIONS: `${API_BASE_URL}/api/consultant-select-options`,
    PROGRAMS: `${API_BASE_URL}/api/programs`,

    // Suggestions
    SUGGESTIONS: `${API_BASE_URL}/api/suggestions`,
    SUGGESTIONS_ADMIN: `${API_BASE_URL}/api/suggestions/admin`,

    // Consultant Vulnerabilities
    CONSULTANT_VULNERABILITIES: `${API_BASE_URL}/api/consultant/vulnerabilities`,

    // Admin Dashboard
    ADMIN_DASHBOARD_STATS: `${API_BASE_URL}/api/admin/dashboard-stats`,
    ADMIN_GROWTH_TREND: `${API_BASE_URL}/api/admin/growth-trend`,
    API_HEALTH: `${API_BASE_URL}/api/health`
  },
  ENVIRONMENT
};

// Helper function to get full API URL
export const getApiUrl = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`;
};

// Helper function to check if running in development
export const isDevelopment = (): boolean => {
  return ENVIRONMENT === 'development';
};

// Helper function to check if running in production
export const isProduction = (): boolean => {
  return ENVIRONMENT === 'production';
};

/** Response from GET /api/users/admin/student-options (distinct courses and years per course for Assign Group) */
export interface StudentOptionsResponse {
  courses: string[];
  yearByCourse: Record<string, string[]>;
}

/** Response from GET /api/users/admin/filter-options (distinct filter values for Enterprise Reports) */
export interface FilterOptionsResponse {
  regions: string[];
  provinces: string[];
  cities: string[];
  barangays: string[];
  ageRanges: string[];
  courses: string[];
  yearLevels: string[];
  schools: string[];
}
